import { useEffect, useState } from "react";
import { createPortal } from "react-dom";

const DESKTOP_WIDTH = "100%";
const MOBILE_WIDTH = "390px";

/** Per-quiz "Preview" button: opens the live hosted quiz in an in-app modal
 * iframe with analytics suppressed (?preview=1), mirroring the mars-men
 * pre-publish preview. The iframe loads the hosted page in its own document so
 * branding, fonts, and shadow-DOM render exactly as a real visitor sees them.
 *
 * The overlay is portaled to <body>: this button lives inside `.rise`
 * containers whose load animation retains a `transform`, and a transformed
 * ancestor becomes the containing block for `position: fixed` descendants —
 * which would otherwise trap the "full-screen" overlay inside that small card. */
export function PreviewButton({ slug }: { slug: string }) {
  const [open, setOpen] = useState(false);
  const [mobile, setMobile] = useState(false);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  if (!slug) return null;

  const src = `${window.location.origin}/hosted.html?quiz=${encodeURIComponent(slug)}&preview=1`;

  return (
    <>
      <button type="button" className="btn-outline" onClick={() => setOpen(true)}>
        Preview
      </button>
      {open && createPortal(
        <div
          className="fixed inset-0 z-50 flex flex-col bg-black/70 p-4"
          onClick={() => setOpen(false)}
        >
          <div
            role="dialog"
            aria-modal="true"
            aria-label={`Preview ${slug}`}
            className="mx-auto flex h-full w-full max-w-5xl flex-col overflow-hidden rounded-lg bg-white"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between gap-3 border-b border-line bg-ink-0 p-3">
              <span className="font-mono text-sm text-text">{slug}</span>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  className="btn-outline"
                  aria-pressed={!mobile}
                  onClick={() => setMobile(false)}
                >
                  Desktop
                </button>
                <button
                  type="button"
                  className="btn-outline"
                  aria-pressed={mobile}
                  onClick={() => setMobile(true)}
                >
                  Mobile
                </button>
                <button
                  type="button"
                  className="btn-outline"
                  aria-label="Close preview"
                  onClick={() => setOpen(false)}
                >
                  ✕
                </button>
              </div>
            </div>
            <div className="flex flex-1 justify-center overflow-auto bg-gray-100">
              <div
                data-testid="preview-frame"
                className="h-full w-full"
                style={{ maxWidth: mobile ? MOBILE_WIDTH : DESKTOP_WIDTH }}
              >
                <iframe
                  title={`Preview of ${slug}`}
                  src={src}
                  sandbox="allow-scripts allow-same-origin"
                  className="h-full w-full border-0"
                />
              </div>
            </div>
          </div>
        </div>,
        document.body,
      )}
    </>
  );
}
